/**
 * constants/themes.ts — All built-in color themes for the app.
 *
 * HOW THEMING WORKS:
 *   1. The user picks a theme (id) and a mode (light | dark) via the Theme Picker.
 *   2. App.tsx reads the selected theme from this array and the chosen palette
 *      (light or dark), then writes each color as a CSS custom property on
 *      the <html> element — e.g., `--bg-primary: #f8fafc`.
 *   3. Every component in the app reads those CSS variables instead of
 *      hard-coding colors, so a theme change takes effect instantly everywhere.
 *
 * HOW TO ADD A NEW THEME:
 *   - Copy any existing theme object below.
 *   - Give it a unique `id` (next integer), a unique `name`, and define both
 *     a `light` and a `dark` palette with all 7 required color values.
 *   - The new theme will automatically appear in the Theme Modal — no other
 *     changes are needed.
 *
 * Color fields (see ThemePalette in src/types/index.ts for full descriptions):
 *   bgPrimary      — main page background
 *   bgSecondary    — cards / panels / inputs background
 *   textPrimary    — main body text
 *   textSecondary  — subdued / label text
 *   accent         — buttons, active states, highlights
 *   accentSecondary— hover states, gradients alongside `accent`
 *   border         — dividers and outline borders
 */

import { Theme } from "../types";

export const themes: Theme[] = [
  // ── 0 · Default ────────────────────────────────────────────────────────────
  // Clean blue-tinted neutral palette — the out-of-the-box look.
  {
    id: 0,
    name: "Default",
    light: {
      bgPrimary: "#f8fafc",
      bgSecondary: "#ffffff",
      textPrimary: "#0f172a",
      textSecondary: "#64748b",
      accent: "#3b82f6",
      accentSecondary: "#2563eb",
      border: "#e2e8f0",
    },
    dark: {
      bgPrimary: "#0f172a",
      bgSecondary: "#1e293b",
      textPrimary: "#f8fafc",
      textSecondary: "#94a3b8",
      accent: "#60a5fa",
      accentSecondary: "#3b82f6",
      border: "#334155",
    },
  },

  // ── 1 · Ocean ───────────────────────────────────────────────────────────────
  // Sky-blue palette inspired by water and open skies.
  {
    id: 1,
    name: "Ocean",
    light: {
      bgPrimary: "#f0f9ff",
      bgSecondary: "#ffffff",
      textPrimary: "#0c4a6e",
      textSecondary: "#0ea5e9",
      accent: "#0284c7",
      accentSecondary: "#0ea5e9",
      border: "#bae6fd",
    },
    dark: {
      bgPrimary: "#0c4a6e",
      bgSecondary: "#075985",
      textPrimary: "#f0f9ff",
      textSecondary: "#bae6fd",
      accent: "#38bdf8",
      accentSecondary: "#0ea5e9",
      border: "#0369a1",
    },
  },

  // ── 2 · Forest ──────────────────────────────────────────────────────────────
  // Earthy green palette — calm and natural.
  {
    id: 2,
    name: "Forest",
    light: {
      bgPrimary: "#f0fdf4",
      bgSecondary: "#ffffff",
      textPrimary: "#14532d",
      textSecondary: "#16a34a",
      accent: "#15803d",
      accentSecondary: "#16a34a",
      border: "#bbf7d0",
    },
    dark: {
      bgPrimary: "#14532d",
      bgSecondary: "#166534",
      textPrimary: "#f0fdf4",
      textSecondary: "#bbf7d0",
      accent: "#4ade80",
      accentSecondary: "#22c55e",
      border: "#14532d",
    },
  },

  // ── 3 · Sunset ──────────────────────────────────────────────────────────────
  // Warm orange-red palette — energetic and fiery.
  {
    id: 3,
    name: "Sunset",
    light: {
      bgPrimary: "#fff7ed",
      bgSecondary: "#ffffff",
      textPrimary: "#7c2d12",
      textSecondary: "#ea580c",
      accent: "#c2410c",
      accentSecondary: "#ea580c",
      border: "#ffedd5",
    },
    dark: {
      bgPrimary: "#431407",
      bgSecondary: "#7c2d12",
      textPrimary: "#fff7ed",
      textSecondary: "#fdba74",
      accent: "#fb923c",
      accentSecondary: "#f97316",
      border: "#7c2d12",
    },
  },

  // ── 4 · Lavender ────────────────────────────────────────────────────────────
  // Soft purple palette — elegant and relaxing.
  {
    id: 4,
    name: "Lavender",
    light: {
      bgPrimary: "#faf5ff",
      bgSecondary: "#ffffff",
      textPrimary: "#581c87",
      textSecondary: "#9333ea",
      accent: "#7e22ce",
      accentSecondary: "#9333ea",
      border: "#e9d5ff",
    },
    dark: {
      bgPrimary: "#3b0764",
      bgSecondary: "#581c87",
      textPrimary: "#faf5ff",
      textSecondary: "#e9d5ff",
      accent: "#a855f7",
      accentSecondary: "#c084fc",
      border: "#581c87",
    },
  },

  // ── 5 · Rose ────────────────────────────────────────────────────────────────
  // Deep pink/crimson palette — bold and romantic.
  {
    id: 5,
    name: "Rose",
    light: {
      bgPrimary: "#fff1f2",
      bgSecondary: "#ffffff",
      textPrimary: "#881337",
      textSecondary: "#e11d48",
      accent: "#be123c",
      accentSecondary: "#e11d48",
      border: "#fecdd3",
    },
    dark: {
      bgPrimary: "#881337",
      bgSecondary: "#9f1239",
      textPrimary: "#fff1f2",
      textSecondary: "#fecdd3",
      accent: "#fb7185",
      accentSecondary: "#f43f5e",
      border: "#881337",
    },
  },

  // ── 6 · Coffee ──────────────────────────────────────────────────────────────
  // Warm amber/brown palette — cozy and rich.
  {
    id: 6,
    name: "Coffee",
    light: {
      bgPrimary: "#fefce8",
      bgSecondary: "#ffffff",
      textPrimary: "#422006",
      textSecondary: "#a16207",
      accent: "#854d0e",
      accentSecondary: "#a16207",
      border: "#fde047",
    },
    dark: {
      bgPrimary: "#422006",
      bgSecondary: "#713f12",
      textPrimary: "#fefce8",
      textSecondary: "#fde047",
      accent: "#eab308",
      accentSecondary: "#facc15",
      border: "#713f12",
    },
  },

  // ── 7 · Midnight ────────────────────────────────────────────────────────────
  // Deep indigo palette — mysterious and focused.
  {
    id: 7,
    name: "Midnight",
    light: {
      bgPrimary: "#e0e7ff",
      bgSecondary: "#ffffff",
      textPrimary: "#1e1b4b",
      textSecondary: "#4338ca",
      accent: "#3730a3",
      accentSecondary: "#4338ca",
      border: "#c7d2fe",
    },
    dark: {
      bgPrimary: "#020617",
      bgSecondary: "#1e1b4b",
      textPrimary: "#e0e7ff",
      textSecondary: "#a5b4fc",
      accent: "#6366f1",
      accentSecondary: "#818cf8",
      border: "#1e1b4b",
    },
  },

  // ── 8 · Cyber ───────────────────────────────────────────────────────────────
  // Neon cyan/green on near-black — futuristic cyberpunk look.
  {
    id: 8,
    name: "Cyber",
    light: {
      bgPrimary: "#f5f5f5",
      bgSecondary: "#ffffff",
      textPrimary: "#262626",
      textSecondary: "#525252",
      accent: "#00d2ff",
      accentSecondary: "#3a7bd5",
      border: "#d4d4d4",
    },
    dark: {
      bgPrimary: "#0a0a0a",
      bgSecondary: "#171717",
      textPrimary: "#ededed",
      textSecondary: "#a3a3a3",
      accent: "#00ff9d",
      accentSecondary: "#00d2ff",
      border: "#262626",
    },
  },

  // ── 9 · Monochrome ──────────────────────────────────────────────────────────
  // Pure black-and-white — maximum contrast, zero color distractions.
  {
    id: 9,
    name: "Monochrome",
    light: {
      bgPrimary: "#ffffff",
      bgSecondary: "#f3f4f6",
      textPrimary: "#000000",
      textSecondary: "#4b5563",
      accent: "#1f2937",
      accentSecondary: "#4b5563",
      border: "#e5e7eb",
    },
    dark: {
      bgPrimary: "#000000",
      bgSecondary: "#111827",
      textPrimary: "#ffffff",
      textSecondary: "#9ca3af",
      accent: "#e5e7eb",
      accentSecondary: "#d1d5db",
      border: "#374151",
    },
  },
];
